import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../config/api";

const FeedbackFormContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  .form-wrapper {
    display: flex;
    flex-direction: row;
    background: #ffffff;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    width: 90%;
    max-width: 1000px;
    max-height: 90%;
    position: relative;
  }

  .form-left {
    flex: 1;
   background: linear-gradient(45deg, #003580,#07b6f2);
    color: #ffffff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 30px;
    text-align: center;
    overflow: auto;
  }

  .form-left h2 {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }

  .form-left p {
    font-size: 1.1rem;
    line-height: 1.9;
    font-weight: 500;
    text-align: center;

  }

  .form-right {
    flex: 2;
    display: flex;
    flex-direction: column;
    padding: 30px;
    overflow-y: auto;
  }

  .form-header {
    font-size: 1.5rem;
    font-weight: 700;
    color: #064e8e;
    margin-bottom: 1rem;
    text-align: center;
  }

  .form-group {
    margin-bottom: 1.5rem;
  }

  label {
    font-size: 1rem;
    font-weight: 600;
    color: #064e8e;
    margin-bottom: 0.5rem;
    display: block;
  }

  input,
  textarea {
    width: 100%;
    padding: 0.9rem;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
    outline: none;
    transition: border-color 0.3s ease;
  }

  input:focus,
  textarea:focus {
    border-color: #064e8e;
  }

  textarea {
    resize: none;
  }

  .buttons {
    display: flex;
    justify-content: space-between;
    gap: 15px;
    margin-top: 1.5rem;
  }

  button {
    padding: 0.9rem;
    font-size: 1rem;
    font-weight: bold;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    flex: 1;
  }

  .submit-button {
    background-color: #064e8e;
    color: #ffffff;
  }

  .submit-button:hover:not(:disabled) {
    background-color: #042c5c;
  }

  .submit-button:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  .cancel-button {
    background-color: #f44336;
    color: #ffffff;
  }

  .cancel-button:hover {
    background-color: #d32f2f;
  }

  .close-button {
    position: absolute;
    top: 15px;
    right: 15px;
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #555555;
    cursor: pointer;
    transition: transform 0.3s ease;
  }

  .close-button:hover {
    transform: rotate(90deg);
  }
`;

const FeedbackForm = ({ onClose = () => {} }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    companyName: "",
    designation: "",
    email: "",
    feedback: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value }); // Removed .trim() here
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const trimmedData = {
      companyName: formData.companyName.trim(),
      designation: formData.designation.trim(),
      email: formData.email.trim(),
      feedback: formData.feedback.trim(),
    };

    const { companyName, designation, email, feedback } = trimmedData;

    // Check for empty fields after trimming spaces
    if (!companyName || !designation || !email || !feedback) {
      toast.error("All fields are required.");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/feedback.php`, trimmedData);
      toast.success(response.data.message || "Feedback submitted successfully.");
      setFormData({ companyName: "", designation: "", email: "", feedback: "" });
      // Close the modal after a short delay so user can see the toast
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit feedback. Please try again.");
    }

    setIsSubmitting(false);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <FeedbackFormContainer>
      <Helmet>
        <title>Share Your Feedback | NexTech PLM Solutions</title>
        <meta name="description" content="Send your feedback to NexTech PLM Solutions about project delivery, Teamcenter support, and your overall experience working with our PLM experts in Pune, India." />
        <meta name="keywords" content="NexTech PLM feedback, PLM service feedback form, Teamcenter support feedback, customer experience NexTech, PLM project review" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://nextechplm.com/feedback" />
        <meta property="og:site_name" content="NexTech PLM Solutions" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Share Your Feedback | NexTech PLM Solutions" />
        <meta property="og:description" content="Send your feedback to NexTech PLM Solutions about project delivery, Teamcenter support, and your overall experience working with our PLM experts in Pune, India." />
        <meta property="og:url" content="https://nextechplm.com/feedback" />
        <meta name="twitter:title" content="Share Your Feedback | NexTech PLM Solutions" />
        <meta name="twitter:description" content="Send your feedback to NexTech PLM Solutions about project delivery, Teamcenter support, and your overall experience working with our PLM experts in Pune, India." />
      </Helmet>
      <div className="form-wrapper">
        <button className="close-button" onClick={handleCancel}>
          &times;
        </button>
        <div className="form-left">
          <h2>We'd Love to Hear From You!</h2>
          <p>
          We value your experience and would love to hear about what made it special.
Please share your thoughts on what you enjoyed most about our services.
Your kind words inspire us to continue delivering the excellence you deserve!
          </p>
        </div>
        <div className="form-right">
          <h2 className="form-header">Share Your Experience With Us</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                name="companyName"
                placeholder="Enter your company name"
                value={formData.companyName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Designation</label>
              <input
                type="text"
                name="designation"
                placeholder="Enter your designation"
                value={formData.designation}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Share Your Feedback</label>
              <textarea
                name="feedback"
                placeholder="Share your feedback"
                rows="4"
                value={formData.feedback}
                onChange={handleChange}
                required
              ></textarea>
            </div>
            <div className="buttons">
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
              <button type="button" className="cancel-button" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </FeedbackFormContainer>
  );
};

export default FeedbackForm;
